<template>
  <header
    class="fixed top-0 left-0 w-full z-50 transition-all duration-300 backdrop-blur-md"
    :class="[
      isScrolled || isWhiteBackground ? 'bg-white/80 shadow-md py-2' : 'py-4',
      isDarkTheme && !isScrolled ? 'text-white' : 'text-gray-900'
    ]"
  >
    <div class="container mx-auto px-4 flex items-center justify-between">
      <!-- LOGO -->
      <router-link to="/" class="flex items-center gap-2 group">
        <ImgComponent
          src="/images/logo_symbole.jpg"
          alt="logo"
          class="h-10 w-10 md:h-12 md:w-12 rounded-lg object-cover shadow-sm group-hover:scale-105 transition-transform"
        />
        <div class="text-xl md:text-2xl font-bold tracking-tight">Parcours Pro</div>
      </router-link>

      <!-- DESKTOP NAVS -->
      <nav class="hidden md:flex items-center gap-8">
        <div v-for="(tab, i) in desktopNavigation" :key="i" class="relative group">
          <!-- Main Link -->
          <router-link
            v-if="!tab.children"
            :to="tab.id"
            class="font-medium transition-colors py-2 flex items-center gap-1"
            :class="[
              isDarkTheme && !isScrolled ? 'hover:text-primary-300' : 'hover:text-primary',
              $route.path === tab.id ? (isDarkTheme && !isScrolled ? 'text-primary-300' : 'text-primary') : ''
            ]"
          >
            {{ tab.name }}
          </router-link>

          <!-- Dropdown Trigger -->
          <div
            v-else
            class="font-medium transition-colors py-2 cursor-pointer flex items-center gap-1"
            :class="isDarkTheme && !isScrolled ? 'hover:text-primary-300' : 'hover:text-primary'"
          >
            {{ tab.name }}
            <span
              class="w-1.5 h-1.5 border-b-2 border-r-2 transform rotate-45 mb-1 transition-transform group-hover:rotate-[225deg]"
              :class="isDarkTheme && !isScrolled ? 'border-white' : 'border-gray-900'"
            ></span>
          </div>

          <!-- Dropdown Menu -->
          <div
            v-if="tab.children"
            class="absolute top-full left-0 mt-2 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 p-2 text-gray-900"
          >
            <router-link
              v-for="child in tab.children"
              :key="child.id"
              :to="child.id"
              class="block px-4 py-3 rounded-xl hover:bg-primary-50 hover:text-primary transition-all flex items-center gap-3"
            >
              <div v-if="child.icon" class="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                 <ColorImg :src="child.icon" class="w-4 h-4 bg-primary" />
              </div>
              <div class="flex flex-col">
                <span class="font-bold text-sm">{{ child.name }}</span>
                <span v-if="child.desc" class="text-[10px] text-gray-400 leading-tight">{{ child.desc }}</span>
              </div>
            </router-link>
          </div>
        </div>
      </nav>

      <!-- ACTIONS -->
      <!-- <div class="hidden md:flex items-center gap-4">
        <button
          @click="$bus.emit($constants.EVENTS.REGISTRATION?.OPEN)"
          class="px-6 py-2.5 bg-primary text-white font-bold rounded-full hover:bg-primary-600 transition-all shadow-lg shadow-primary/20 text-sm"
        >
          S'inscrire
        </button>
      </div> -->

      <!-- MOBILE HAMBURGER -->
      <button
        class="md:hidden p-2 rounded-xl"
        :class="isDarkTheme && !isScrolled ? 'bg-white/10' : 'bg-gray-100'"
        @click="$refs.navDialog.show()"
      >
        <ColorImg
          src="/images/icons/hamburger.svg"
          class="w-6 h-6"
          :class="isDarkTheme && !isScrolled ? 'bg-white' : 'bg-gray-900'"
        />
      </button>
    </div>

    <!-- MOBILE MENU -->
    <DialogComponent type="ui-less" ref="navDialog" v-slot="{ zIndex, dialogVisible }">
      <div v-if="dialogVisible" class="fixed inset-0" :style="{ zIndex }">
        <Transition name="fade" appear>
          <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" @click="$refs.navDialog.hide()"></div>
        </Transition>
        <Transition name="slide-down" appear>
          <div class="absolute top-0 left-0 w-full bg-white rounded-b-[2rem] shadow-2xl p-6 pt-20 overflow-y-auto max-h-[90vh]">
            <div class="space-y-6">
              <div v-for="item in navigation" :key="item.name">
                <div v-if="!item.children">
                  <router-link :to="item.id" class="text-xl font-bold text-gray-900 block" @click="$refs.navDialog.hide()">{{ item.name }}</router-link>
                </div>
                <div v-else class="space-y-4">
                  <span class="text-xs font-black text-gray-400 uppercase tracking-widest">{{ item.name }}</span>
                  <div class="grid gap-4">
                    <router-link
                      v-for="child in item.children"
                      :key="child.id"
                      :to="child.id"
                      class="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl"
                      @click="$refs.navDialog.hide()"
                    >
                      <div class="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                        <ColorImg :src="child.icon" class="w-5 h-5 bg-primary" />
                      </div>
                      <span class="font-bold text-gray-900">{{ child.name }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
              <button
                @click="() => { $bus.emit($constants.EVENTS.REGISTRATION?.OPEN); $refs.navDialog.hide(); }"
                class="w-full py-4 bg-primary text-white font-bold rounded-2xl shadow-xl shadow-primary/20"
              >
                Commencer maintenant
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </DialogComponent>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const isScrolled = ref(false);
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const isDarkTheme = computed(() => {
  // Orienta and certifications also have a dark hero background, so they need white text
  return [
    '/parcours-entrepreneur', 
    '/orienta', 
    '/certifications/soft-skills-pro', 
    '/certifications/disc-10x', 
    '/certifications/orienta-coach'
  ].includes(route.path);
});

const isWhiteBackground = computed(() => {
  // Remove orienta from white background list as it needs transparent/dark theme
  return false;
});

const navigation = [
  { name: 'Accueil', id: '/' },
  { name: 'Parcours Employabilité', id: '/parcours-employabilite' },
  { name: 'Parcours Entrepreneur', id: '/parcours-entrepreneur' },
  { name: 'Formations Certifiantes', id: '/certifications' },
  { name: 'Orienta Tour', id: '/orienta' }
];

const desktopNavigation = computed(() => {
  return navigation.filter(item => item.name !== 'Accueil');
});
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
